import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Skeleton, useMediaQuery } from '@mui/material';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { Button, Link } from 'gatsby-theme-material-ui';
import Feature from '../../../shared/Feature/Feature';
import List from '../../../shared/List/List';
import Section from '../../../shared/Section/Section';
import LocationLabel from '../../../shared/AnimatedLabel/LocationLabel';
import ModalBlock from '../../../shared/ModalBlock/ModalBlock';
import ConsultationModal from '../../../shared/Modals/ConsultationModal';
import ProgramRequestModal from '../../../shared/Modals/ProgramRequestModal';
import { parseDate } from '../../../helpers/lib';
import { NotificationChip } from '../../../shared/NotificationChip/NotificationChip';
import styles, { createImgStyles } from './narrowStyles';

const colorThemes = {
  dark: {
    mainColor: 'kit.text.mainInverse',
    titleColor: 'kit.text.mainInverse',
    titleSize: '72px',
    iconColor: 'kit.text.accent',
    listColor: 'rgba(41, 237, 255, 0.8)',
    typeTextColor: 'rgba(255, 255, 255, 0.5)',
    weeksTextColor: 'kit.background.white',
    weeksPosition: {
      left: '7%',
      top: '45%',
    },
    buttonVariant: 'inverse',
  },
  default: {
    mainColor: 'kit.background.black',
    titleColor: 'kit.background.black',
    iconColor: 'kit.background.black',
    listColor: 'kit.text.secondary',
    weeksTextColor: 'kit.background.white',
    weeksPosition: {
      left: '7%',
      top: '45%',
    },
  },
  main: {
    mainColor: 'kit.text.main',
    titleColor: 'kit.text.h',
    iconColor: 'kit.background.main',
    listColor: 'rgba(15, 15, 15, 0.8)',
    weeksTextColor: 'kit.background.white',
    weeksPosition: {
      left: '7%',
      top: '45%',
    },
  },
  lightPurple: {
    mainColor: 'kit.text.main',
    titleColor: 'kit.text.h',
    iconColor: 'kit.background.main',
    listColor: 'rgba(15, 15, 15, 0.8)',
    weeksTextColor: 'kit.text.h',
    weeksPosition: {
      left: '0%',
      top: '30%',
    },
  },
  middlePurple: {
    mainColor: 'kit.text.main',
    titleColor: 'kit.text.main',
    iconColor: 'kit.background.main',
    listColor: 'rgba(15, 15, 15, 0.8)',
    weeksTextColor: 'kit.text.h',
    weeksPosition: {
      left: '0%',
      top: '30%',
    },
  },
};

const formData = {
  title: 'Оставьте ваши контакты для связи',
  subtitle: 'Получить консультацию',
  sentContent: {
    title: 'Успешно записаны на консультацию!',
    message: 'Скоро с вами свяжется куратор',
  },
};

function Greetings({
  theme = 'dark',
  location,
  type,
  title,
  points,
  features,
  image,
  action,
  duration,
  nonstandardImg,
  bg,
  labelTheme,
  extraLeadData,
  requestLeadData,
  isLoading,
  group,
  ymEvents = {},
  isPrecourse,
  isProgram = true,
  buttonLink,
}) {
  const isSm = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isBelowMd = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const [isRequestOpen, setIsRequestOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [groupDateParsedMessage, setGroupDateParsedMessage] = useState(null);

  const colorTheme = colorThemes[theme] || colorThemes.main;

  useEffect(() => {
    if (!isLoading) {
      if (group?.startDate) {
        setStartDate(() => {
          const parsedDate = parseDate(group.startDate);
          setGroupDateParsedMessage(
            `записаться на ${parsedDate[1]} ${parsedDate[2]}`,
          );

          return parsedDate;
        });

        setEndDate(() => parseDate(group.endDate));
      } else {
        setGroupDateParsedMessage(
          isProgram ? 'записаться' : 'посмотреть программы',
        );
      }
    }
  }, [group, isLoading]);

  return (
    <Section
      bg={bg}
      withoutBg={!bg}
      theme={theme || 'dark'}
      customStyles={{
        padding: {
          xs: '34px 0 40px 0',
          lg: '40px 0 64px 0',
        },
      }}
    >
      <Grid container color={colorTheme.mainColor}>
        <Grid item xs={12} md={6}>
          <Box sx={styles.top}>
            <LocationLabel
              theme={labelTheme}
              type={location.type}
              title={location.title}
            />
            <Typography variant="desktopP2" sx={styles.type}>
              {type}
            </Typography>
          </Box>
          <Typography
            component="p"
            variant="mobileH0"
            sx={{ ...styles.title, color: colorTheme.titleColor }}
          >
            {Array.isArray(title)
              ? title.map((item, index) => (
                  <Typography
                    key={index}
                    component="span"
                    sx={{ display: 'block' }}
                  >
                    {item}
                  </Typography>
                ))
              : title}
          </Typography>

          <Box sx={{ ...styles.list, color: colorTheme.listColor }}>
            <List
              list={points}
              iconColor={colorTheme.iconColor}
              size="xsmall"
            />
          </Box>

          {features && (
            <Box sx={styles.features} component="ul">
              {features.map((feature, index) => (
                <Box key={index} component="li" sx={styles.featureItem}>
                  <Feature {...feature} theme={theme} />
                </Box>
              ))}
            </Box>
          )}
        </Grid>
        <Grid item xs={12} md={6} sx={styles.imageBlock}>
          <Box sx={createImgStyles(nonstandardImg)}>
            <GatsbyImage
              image={getImage(image)}
              imgStyle={styles.image}
              alt=""
            />
            {duration && (
              <Box sx={{ ...styles.duration, ...colorTheme.weeksPosition }}>
                <Typography
                  sx={styles.number}
                  color={colorTheme.weeksTextColor}
                >
                  {duration.number}
                </Typography>
                <Typography
                  sx={styles.currency}
                  color={colorTheme.weeksTextColor}
                >
                  {duration.currency}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ mt: !isProgram && !isBelowMd ? -5 : 0 }}>
          {isPrecourse && <NotificationChip group={group} />}
          {isProgram && (
            <Box sx={styles.buttonsBlock}>
              {!isPrecourse && (
                <Button
                  variant="kitPrimary"
                  size={isSm ? 'large' : 'medium'}
                  component="span"
                  sx={styles.button}
                  onClick={() => setIsRequestOpen(true)}
                >
                  {!isLoading && groupDateParsedMessage ? (
                    groupDateParsedMessage
                  ) : (
                    <Skeleton width="90px" height="17.5px" />
                  )}
                </Button>
              )}
              {isPrecourse && (
                <Link
                  underline="none"
                  href={`/payment/?leartype=${group.courseName}.prepare.ru`}
                  target={'_blank'}
                >
                  <Button
                    variant="kitPrimary"
                    size={isSm ? 'large' : 'medium'}
                    component="span"
                    sx={styles.button}
                  >
                    {!isLoading && groupDateParsedMessage ? (
                      `Купить за ${group.finalPrice} ₽`
                    ) : (
                      <Skeleton width="90px" height="17.5px" />
                    )}
                  </Button>
                </Link>
              )}
              <Button
                variant={theme === 'dark' ? 'inverse' : 'outlined'}
                size="medium"
                component="span"
                sx={styles.button}
                onClick={() => setIsConsultationOpen(true)}
              >
                {action}
              </Button>
            </Box>
          )}
          {!isProgram && (
            <Box sx={styles.buttonsBlock}>
              <Link underline="none" href={buttonLink}>
                <Button
                  variant="kitPrimary"
                  size={isSm ? 'large' : 'medium'}
                  component="span"
                  sx={styles.button}
                >
                  {action}
                </Button>
              </Link>
              <Button
                variant="outlined"
                size="medium"
                component="span"
                sx={styles.button}
                onClick={() => setIsConsultationOpen(true)}
              >
                Записаться на обучение
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>

      <ModalBlock
        isOpen={isConsultationOpen}
        handleClose={() => setIsConsultationOpen(false)}
      >
        <ConsultationModal
          extraLeadData={extraLeadData}
          formData={formData}
          ymSubmitEventType={ymEvents.consultationFormSubmit}
        />
      </ModalBlock>

      <ModalBlock
        isOpen={isRequestOpen}
        handleClose={() => setIsRequestOpen(false)}
      >
        <ProgramRequestModal
          extraLeadData={requestLeadData}
          startDate={startDate}
          endDate={endDate}
          date={group?.startDate}
          formData={{ title, subtitle: type }}
          ymSubmitEventType={ymEvents.signUpFormSubmit}
        />
      </ModalBlock>
    </Section>
  );
}

export default Greetings;
